// ── THE money question, ONE derivation ──────────────────────────────────────
// « Combien ? » has three answers, and every surface that wins a deal (the
// « Travaux acceptés » door on the job page, the desk's work doors) spells its
// payload through here so no two can disagree:
//   template    — « Soumission type »: won_template instantiates the type's
//                 template at the mini form's numbers and accepts it at birth.
//   priced      — « Montant convenu »: won_line mints the custom accepted
//                 one-liner (label empty ⇒ the server falls back to the
//                 type's name; taxable/kind/description are the line's own).
//   no_proposal — « Sans soumission »: the explicit skip_proposal escape,
//                 never reachable through silence.
// (It used to live in the intake composable; the intake no longer asks the
// money question — design 165 — the win door does, on the page.)
import { toDecimal } from '@/lib/number'
import type { TemplateLineInput } from '@/composables/useTemplatePricing'
import type { LineKind } from '@/lib/lineItemKinds'

export type AgreementMode = 'template' | 'priced' | 'no_proposal'

export interface AgreementDraft {
  mode: AgreementMode
  amount?: number | null
  label?: string
  taxable?: boolean
  kind?: LineKind
  description?: string
}

export interface WonAttributes {
  skip_proposal: boolean
  won_line?: { amount: string; label?: string; taxable: boolean; kind: LineKind; description?: string }
  won_template?: { lines: TemplateLineInput[]; proposal_template_id?: string }
}

export const commitmentAttributes = (
  agreement: AgreementDraft,
  templateLines: TemplateLineInput[] = [],
  // The chosen offer — required when the type carries several (the server
  // takes the sole one unnamed).
  proposalTemplateId: string | null = null,
): WonAttributes => {
  if (agreement.mode === 'no_proposal') return { skip_proposal: true }
  if (agreement.mode === 'template') {
    return {
      skip_proposal: false,
      won_template: {
        lines: templateLines,
        ...(proposalTemplateId ? { proposal_template_id: proposalTemplateId } : {}),
      },
    }
  }
  // A priced submit with no amount is unreachable (the win door requires it) —
  // and if it ever slips through, "0" fails LOUD server-side rather than
  // silently becoming a skip.
  return {
    skip_proposal: false,
    won_line: {
      amount: toDecimal(agreement.amount ?? 0),
      taxable: agreement.taxable ?? true,
      kind: agreement.kind ?? 'labor',
      ...(agreement.label?.trim() ? { label: agreement.label.trim() } : {}),
      ...(agreement.description?.trim() ? { description: agreement.description.trim() } : {}),
    },
  }
}
